import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../hooks/useLocale';
import { getReadinessStatus, getTodayMetricValue } from './calendarHelpers';
import './calendar-readiness-summary.css';

/** Selected-day summary; the compact day-list widget keeps its existing layout. */
export function CalendarReadinessSummary({ today }: { today: Record<string, unknown> }) {
  const { t, i18n } = useTranslation();
  const isPersian = i18n.language.startsWith('fa');
  const { locale, dir } = useLocale();
  const readiness = getTodayMetricValue(today, ['readiness', 'ctl']);
  const fatigue = getTodayMetricValue(today, ['fatigue', 'atl']);
  const recovery = getTodayMetricValue(today, ['recovery', 'tsb']);
  const status = getReadinessStatus(readiness);
  const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
  const phases = ['rest', 'coast', 'build', 'prime'] as const;
  const phaseLabels = {
    rest: t('calendar.readiness.status.rest'),
    coast: t('calendar.readiness.compactPhase.coast'),
    build: t('calendar.readiness.compactPhase.build'),
    prime: t('calendar.readiness.status.prime'),
  };
  const metrics = [
    { label: t('readiness.scoreLabel'), value: readiness },
    { label: t('calendar.readiness.fatigue'), value: fatigue },
    { label: t('calendar.readiness.recovery'), value: recovery },
  ];

  return (
    <section className={`cal-readiness-summary${isPersian ? ' cal-readiness-summary--fa' : ''}`} dir={dir} aria-label={t('calendar.readiness.title')}>
      <header className="cal-readiness-summary__header">
        <h3 title={t(status.labelKey)}>{isPersian ? 'فاز ' : ''}{phaseLabels[status.tone as keyof typeof phaseLabels] ?? t(status.labelKey)}</h3>
        <Link className="cal-readiness-summary__details" to="/charts?chart=pmc_report">
          {t('calendar.readiness.details')}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
        </Link>
      </header>
      <ol className="cal-readiness-summary__phases">
        {phases.map((phase, index) => (
          <li key={phase} data-phase={phase} className={phase === status.tone ? 'is-active' : undefined} aria-current={phase === status.tone ? 'step' : undefined}>
            <span className="cal-readiness-summary__segment" aria-hidden="true">
              {phase === status.tone && (
                <bdi className="cal-readiness-summary__score">{number.format(readiness)}</bdi>
              )}
            </span>
            {index < phases.length - 1 && (
              <svg className="cal-readiness-summary__arrow" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <path d="M3 8h9M8 4l4 4-4 4" />
              </svg>
            )}
            <span>{phaseLabels[phase]}</span>
          </li>
        ))}
      </ol>
      <footer className="cal-readiness-summary__footer">
        <dl className="cal-readiness-summary__metrics">
          {metrics.map(({ label, value }) => (
            <div key={label}><dt>{label}</dt><dd className={value < 0 ? 'metric-value--negative' : undefined}>{number.format(value)}</dd></div>
          ))}
        </dl>
      </footer>
    </section>
  );
}
